import { t } from 'i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';

import { VersionDto } from '@/api-sdk';
import { iconCheckmark } from '@/components/icons';
import { MobileController } from '@/controllers';
import { baseStyles, collapsibleOptionsStyles } from '@/styles';

@customElement('fg-collapsible-version-option')
export class FgCollapsibleVersionOption extends LitElement {
  @property({ type: Object })
  version?: VersionDto;

  @property({ type: Boolean })
  focussed = false;

  private buttonRef: Ref<HTMLButtonElement> = createRef();

  protected mobileController = new MobileController(this);

  updateComponent(
    queryString: string | null | undefined,
    selectedVersion?: string,
  ) {
    if (queryString === null || queryString === undefined) {
      console.error('Missing query string');
      return;
    }

    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );

    if (selectedVersion !== undefined) {
      this.dispatchEvent(
        new CustomEvent('handle-announcement', {
          composed: true,
          detail: {
            announcement: `${selectedVersion} ${t('labels.selected')}`,
          },
        }),
      );
    }
  }

  render() {
    if (this.version === undefined) return nothing;

    const { isMobile } = this.mobileController;
    const buttonRef = ref(this.buttonRef);

    this.updateComplete.then(() => {
      if (this.focussed && !isMobile) {
        this.buttonRef.value?.focus();
      }
    });

    return html`
      <li aria-hidden=${Boolean(this.version?.disabled)}>
        <span
          aria-hidden="true"
          class="checkmark ${classMap({
            selected: this.version?.selected,
          })}"
        >
          ${iconCheckmark}
        </span>
        <button
          ${buttonRef}
          class="${classMap({
            selected: this.version?.selected,
          })}"
          ?disabled=${Boolean(this.version?.disabled)}
          @click=${() =>
            this.updateComponent(
              this.version?.queryString,
              this.version?.version,
            )}
        >
          ${this.version?.version}
          <span class="sr-only">
            ${this.version?.selected ? t('labels.selected') : nothing}
          </span>
        </button>
      </li>
    `;
  }

  static styles = [
    baseStyles,
    collapsibleOptionsStyles,
    css`
      li {
        position: relative;
      }

      button {
        background-color: #fff;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        height: 58px;
        display: grid;
        font-weight: 700;
        place-items: center;
        width: 100%;
      }

      button:focus {
        outline: 2px solid var(--fg-gray--800, var(--fg-fallback-gray--800));
        outline-offset: 2px;
      }

      button:hover {
        border: 1px solid var(--fg-gray--500, var(--fg-fallback-gray--500));
        text-decoration: underline;
      }

      button.selected {
        background-color: var(
          --fg-config-selected--light,
          var(--fg-fallback-config-selected--light)
        );
        border: 3px solid
          var(--fg-config-selected, var(--fg-fallback-config-selected));
      }

      button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `,
  ];
}
